import { Box, Button, CircularProgress, Grid, Paper } from '@mui/material';
import { GROUPBY } from '../../../constants/Reports';
import SelectRebuild from '../../../components/Inputs/SelectRebuild';
import { ExportCsvButton } from '../../../components/Buttons/ExportCsvButton';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import {
  selectCSVWoburnMunisOvertimeReport,
  selectEmployeeData,
  selectWoburnMunisOvertimeReport,
} from '../../../store/ReportsDetailsSlice/selectors';
import { useDateRangeFilter } from '../hooks/useDateRangeFilter';
import ReportsHeader from '../../../components/Reports/ReportsHeader';
import { FilterPanel } from '../../../components/Reports/FilterPanel';
import DataNotFound from '../../../components/Reports/DataNotFound';
import { useEffect, useMemo } from 'react';
import {
  getCSVWoburnMunisOvertimeReportData,
  getEmployeeList,
  getWoburnMunisOvertimeReportData,
  setWoburnMunisOvertimeCSVReportData,
} from '../../../store/ReportsDetailsSlice/actions';
import moment from 'moment';
import { saveCsvToFile } from '../../../utils/saveCsvToFile';
import { useRows } from './hooks/useRows';
import { usePrivateRows } from './hooks/usePrivateRows';
import { useColumns } from './hooks/useColumns';
import { Card } from './components/Card';
import useTownRows from './hooks/useTownRows';

const createParams = (param) =>
  param && param.length > 0 ? param.map((par) => par.id) : ['ALL'];

const isLengthZero = (arr) => arr.length === 0;

const WoburnMunisOvertimeReport = () => {
  const dispatch = useDispatch();
  const { employeeList, loadingEmployeeList } = useSelector(selectEmployeeData);
  const {
    woburnMunisOvertimeReportData,
    isWoburnMunisOvertimeReportDataLoading,
  } = useSelector(selectWoburnMunisOvertimeReport);
  const {
    woburnMunisOvertimeCSVReportData,
    isWoburnMunisOvertimeCSVReportDataLoading,
  } = useSelector(selectCSVWoburnMunisOvertimeReport);
  const { fromDate, toDate, render } = useDateRangeFilter();

  const { register, setValue, watch } = useForm({
    defaultValues: {
      emp_code: [],
      groupby: { id: 'Emp', name: 'Employee' },
    },
  });
  const { emp_code: employees, groupby } = watch();
  const employeeParam = useMemo(() => createParams(employees), [employees]);

  const param = useMemo(() => {
    return {
      start_dt: moment(fromDate).format('YYYY-MM-DD') + ' 00:00:00',
      end_dt: moment(toDate).format('YYYY-MM-DD') + ' 23:59:59',
      sel_order: groupby.id,
      sel_emp: employeeParam,
    };
  }, [fromDate, toDate, groupby, employeeParam]);

  const overtimeColumns = useColumns('overtime', groupby.id);
  const townColumns = useColumns('town', groupby.id);
  const oogColumns = useColumns('oog', groupby.id);
  const stipendsOsColumns = useColumns('stipends_os', groupby.id);
  const stipendsSoColumns = useColumns('stipends_so', groupby.id);
  const privateColumns = useColumns('private', groupby.id);

  const handleDownloadReport = () => {
    const query = { ...param, csv: 1 };
    dispatch(getCSVWoburnMunisOvertimeReportData(query));
  };

  const handleFindClick = () =>
    dispatch(getWoburnMunisOvertimeReportData(param));

  useEffect(() => {
    dispatch(getEmployeeList());
    dispatch(getWoburnMunisOvertimeReportData(param));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (woburnMunisOvertimeCSVReportData.data) {
      saveCsvToFile(
        woburnMunisOvertimeCSVReportData.data,
        woburnMunisOvertimeCSVReportData.filename
      );
      dispatch(
        setWoburnMunisOvertimeCSVReportData({ data: null, filename: '' })
      );
    }
  }, [woburnMunisOvertimeCSVReportData, dispatch]);

  const isGroupByDate = groupby.id === GROUPBY[1].value;

  const overtimeRows = useRows(woburnMunisOvertimeReportData.overtime.data, isGroupByDate);
  const townRows = useTownRows(woburnMunisOvertimeReportData.town.data, isGroupByDate);
  const oogRows = useRows(woburnMunisOvertimeReportData.oog.data, isGroupByDate);
  const privateRows = usePrivateRows(
    woburnMunisOvertimeReportData.private.data,
    isGroupByDate
  );
  const stipendsOsRows = useRows(
    woburnMunisOvertimeReportData.stipends_os.data,
    isGroupByDate
  );
  const stipendsSoRows = useRows(
    woburnMunisOvertimeReportData.stipends_so.data,
    isGroupByDate
  );

  const isNoData =
    isLengthZero(overtimeRows) &&
    isLengthZero(townRows) &&
    isLengthZero(oogRows) &&
    isLengthZero(privateRows) &&
    isLengthZero(stipendsOsRows) &&
    isLengthZero(stipendsSoRows);

  const rawFilterConfig = [
    {
      key: 'group_by',
      label: 'Group By',
      show: GROUPBY.length > 0,
      renderComponent: () => (
        <SelectRebuild
          options={GROUPBY.map((item) => ({
            id: item.value,
            name: item.title,
          }))}
          name="groupby"
          label="Group By"
          multiple={false}
          loading={false}
          register={register}
          setValue={setValue}
          watch={watch}
        />
      ),
    },
    {
      key: 'emp_code',
      label: 'Employee',
      show: true,
      renderComponent: () => (
        <SelectRebuild
          options={employeeList}
          name="emp_code"
          label="Employee"
          multiple={true}
          allOption={{ id: 'ALL', name: 'All' }}
          loading={loadingEmployeeList}
          register={register}
          setValue={setValue}
          watch={watch}
        />
      ),
    },
    {
      key: 'find_button',
      label: '',
      show: true,
      renderComponent: () => (
        <Grid
          item
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: '100%',
            paddingTop: '23px',
          }}
        >
          <Button
            variant="contained"
            onClick={handleFindClick}
            sx={{ whiteSpace: 'nowrap', height: '32px' }}
          >
            Find
          </Button>
        </Grid>
      ),
    },
    {
      key: 'export',
      label: '',
      show: true,
      renderComponent: () => (
        <Grid
          item
          sx={{
            width: '100%',
            display: 'flex',
            gap: 2,
            justifyContent: 'end',
            alignItems: 'center',
            height: '100%',
            paddingTop: '23px',
          }}
        >
          <ExportCsvButton
            onClick={handleDownloadReport}
            disabled={isWoburnMunisOvertimeCSVReportDataLoading}
            text={'Export CSV'}
          />
        </Grid>
      ),
    },
  ];

  const filterConfig = rawFilterConfig.filter((f) => f.show !== false);

  return (
    <Paper className="p-4" sx={{ marginTop: '1rem' }}>
      <ReportsHeader title="Woburn Munis Overtime Report Details" />

      <Box className="mt-4 mb-4" sx={{ width: '100%' }}>
        <Grid container sx={{ width: '100%', mb: '24px' }}>
          <FilterPanel filters={filterConfig} columns={4} />
        </Grid>

        <Grid container sx={{ width: '100%' }}>
          <Grid
            item
            sx={{
              display: 'flex',
              gap: 2,
              width: '100%',
              alignItems: 'center',
            }}
          >
            <Grid
              item
              sx={{ display: 'flex', gap: 2, width: '100%', mt: 'auto' }}
            >
              {render}
            </Grid>
          </Grid>
        </Grid>
      </Box>

      {isWoburnMunisOvertimeReportDataLoading ? (
        <Box
          sx={{ minHeight: '30px' }}
          className="w-10 h-10 d-flex justify-content-center align-items-center"
        >
          <CircularProgress size={32} />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 4, flexDirection: 'column' }}>
          {isNoData ? (
            <DataNotFound />
          ) : (
            <>
              <Card
                columns={overtimeColumns}
                data={overtimeRows}
                title="Overtime"
              />
              <Card columns={townColumns} data={townRows} title="Town" />
              <Card columns={oogColumns} data={oogRows} title="Out Of Grade" />
              <Card columns={privateColumns} data={privateRows} title="Private" />
              <Card
                columns={stipendsOsColumns}
                data={stipendsOsRows}
                title="Stipends OS"
              />
              <Card
                columns={stipendsSoColumns}
                data={stipendsSoRows}
                title="Stipends SO"
              />
            </>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default WoburnMunisOvertimeReport;
